'use client';

import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { ReactNode, useCallback, useEffect, useMemo } from 'react';
import { LoadedSessionContext } from 'supertokens-auth-react/lib/build/recipe/session/types';
import MultiFactorAuth from 'supertokens-auth-react/recipe/multifactorauth';
import Session from 'supertokens-auth-react/recipe/session';

import { RouteLoadingSkeleton } from '@/components/templates/route-loading-skeleton';
import { useGetMyUser } from '@/services/api/auth/get-my-user/hook';
import { notify } from '@/services/notify/notify';
import { getApiErrorStatusCode } from '@/services/react-query/validate-api-error';

import { authContext } from './use-auth-context';

type AuthContentProviderProps = {
  children: ReactNode;
};

/* Safety net: if the who-am-i bootstrap never resolves (e.g. an unresponsive
   /me), bail to sign-in instead of leaving the user on an infinite loader. */
const AUTH_BOOTSTRAP_TIMEOUT_MS = 15_000;

export const AuthContextProvider = ({ children }: AuthContentProviderProps) => {
  const queryClient = useQueryClient();
  const router = useRouter();

  const sessionContext = Session.useSessionContext() as LoadedSessionContext;

  // A session can exist while a required second factor is still pending; SuperTokens
  // redirects to the TOTP factor in that state, so we must not treat it as authenticated.
  const invalidMfa =
    !!sessionContext.accessTokenPayload &&
    MultiFactorAuth.MultiFactorAuthClaim.getValueFromPayload(
      sessionContext.accessTokenPayload
    )?.v === false;

  const hasSession =
    !sessionContext.loading && sessionContext.doesSessionExist && !invalidMfa;

  const {
    data: userData,
    isLoading: isUserDataLoading,
    isError: isUserError,
    error: userError,
  } = useGetMyUser(hasSession);

  // Defense-in-depth: only customers and their collaborators belong in this app
  // (both carry a customer relationId). The backend rejects other identities with
  // 403 (handled below); we still block anything unexpected here rather than hang.
  const relationType = userData?.attributes?.relationType;
  const isNonCustomer =
    !!userData &&
    ((relationType !== 'CUSTOMER' && relationType !== 'COLLABORATOR') ||
      !userData.attributes?.relationId);

  // Detect when a different user has logged in on another tab (their session overwrites
  // the shared localStorage token). Compare the current session's userId against the one
  // stored in this tab's sessionStorage; a mismatch means we're now holding someone
  // else's session and must log out.
  useEffect(() => {
    if (!hasSession) return;

    const storedId = sessionStorage.getItem('dp-auth-user-id');
    if (storedId && storedId !== sessionContext.userId) {
      void Session.signOut()
        .catch(() => undefined)
        .finally(() => {
          localStorage.clear();
          sessionStorage.clear();
          window.location.href = '/auth';
        });
    }
  }, [hasSession, sessionContext.userId]);

  // Persist the authenticated user ID so the mismatch check above has a baseline.
  useEffect(() => {
    if (userData?.id) {
      sessionStorage.setItem('dp-auth-user-id', userData.id);
    }
  }, [userData?.id]);

  const signOut = useCallback(
    (options = { hardRedirect: true }) => {
      void Session.signOut()
        .catch(() => undefined)
        .finally(() => {
          localStorage.clear();
          sessionStorage.clear();
          queryClient.clear();

          if (options.hardRedirect) {
            window.location.href = '/auth';
            return;
          }

          router.push('/auth');
        });
    },
    [queryClient, router]
  );

  // /me failed for a live session — most importantly a 403 for a non-customer
  // (admin/supplier). Sign out and bounce to login rather than leaving them stuck.
  useEffect(() => {
    if (!hasSession || !isUserError) return;

    const status = getApiErrorStatusCode(userError);
    notify.alert.error(
      status === 403
        ? "This account can't access the customer app."
        : 'Could not verify your account. Please sign in again.'
    );
    signOut({ hardRedirect: false });
  }, [hasSession, isUserError, userError, signOut]);

  // Defense-in-depth for the unlikely case /me returns a non-customer with 200.
  useEffect(() => {
    if (hasSession && isNonCustomer) {
      notify.alert.error("This account can't access the customer app.");
      signOut({ hardRedirect: false });
    }
  }, [hasSession, isNonCustomer, signOut]);

  // Fallback for any genuinely stuck bootstrap: if we're still authenticating
  // after the timeout, bail to sign-in.
  useEffect(() => {
    if (!hasSession || !isUserDataLoading) return;

    const timeoutId = setTimeout(() => {
      notify.alert.error('Loading is taking too long. Please sign in again.');
      signOut({ hardRedirect: false });
    }, AUTH_BOOTSTRAP_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [hasSession, isUserDataLoading, signOut]);

  const value = useMemo(
    () => ({
      userId: userData?.id,
      user: userData || undefined,
      customerId: userData?.attributes?.relationId,
      isCollaborator: userData?.attributes?.relationType === 'COLLABORATOR',
      isAuthenticatingUser: hasSession && isUserDataLoading && !userData,
      signOut,
    }),
    [userData, hasSession, isUserDataLoading, signOut]
  );

  // Hold rendering until a valid customer session is confirmed — covers the initial
  // /me load and the brief window while an invalid/non-customer session is being
  // signed out — so customer-scoped pages never mount (and fire failing requests).
  if (hasSession && (isUserDataLoading || isNonCustomer || isUserError)) {
    return <RouteLoadingSkeleton />;
  }

  return <authContext.Provider value={value}>{children}</authContext.Provider>;
};
